import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from 'react'
import { createPortal } from 'react-dom'
import { assetPreviewBlob, type NoteAsset } from '../assets/assetTypes'
import { APP_SETTINGS } from '../config/appSettings'
import { Icon } from './Icon'
import { OVERLAY_PRIORITY, useContextBack } from '../navigation/contextBack'

export interface ImagePreview {
  assetId: string
  alt: string
  source: string
}

interface ImageLightboxProps extends ImagePreview {
  assets: readonly NoteAsset[]
  remoteUrls: ReadonlyMap<string, string>
  onClose: () => void
  loadFullImage?: (asset: NoteAsset) => Promise<Blob>
}

interface Point {
  x: number
  y: number
}

interface ImageTransform {
  scale: number
  x: number
  y: number
}

interface ImageSize {
  height: number
  width: number
}

interface PinchGesture {
  distance: number
  focalX: number
  focalY: number
  scale: number
}

interface PanGesture extends Point {
  pointerId: number
  transformX: number
  transformY: number
}

interface SwipeGesture extends Point {
  pointerId: number
  horizontal: boolean | null
}

const MIN_IMAGE_SCALE = 1
const MAX_IMAGE_SCALE = 5

export function clampImageScale(scale: number): number {
  return Math.min(MAX_IMAGE_SCALE, Math.max(MIN_IMAGE_SCALE, scale))
}

export function calculateFittedImageSize(
  imageWidth: number,
  imageHeight: number,
  stageWidth: number,
  stageHeight: number,
): ImageSize {
  if (
    imageWidth <= 0
    || imageHeight <= 0
    || stageWidth <= 0
    || stageHeight <= 0
  ) return { width: 0, height: 0 }
  const containScale = Math.min(
    stageWidth / imageWidth,
    stageHeight / imageHeight,
  )
  return {
    width: imageWidth * containScale,
    height: imageHeight * containScale,
  }
}

function distanceBetween([first, second]: [Point, Point]): number {
  return Math.hypot(second.x - first.x, second.y - first.y)
}

function centerBetween([first, second]: [Point, Point]): Point {
  return {
    x: (first.x + second.x) / 2,
    y: (first.y + second.y) / 2,
  }
}

export function ImageLightbox({ assetId, alt, source, assets, remoteUrls, onClose, loadFullImage }: ImageLightboxProps) {
  const [current, setCurrent] = useState({ assetId, alt, source })
  const trackRef = useRef<HTMLDivElement>(null)
  const slideRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<Animation | null>(null)
  const dragRef = useRef(0)
  const sourceCache = useRef(new Map([[assetId, source]]))
  const ownedSources = useRef(new Set<string>())
  const [neighbors, setNeighbors] = useState<Record<string, string>>({})
  const fullImageRequests = useRef(new Map<string, Promise<Blob>>())
  const fullImageIds = useRef(new Set<string>())
  const [fullImageStatus, setFullImageStatus] = useState<{ id: string; state: 'loading' | 'ready' | 'failed' } | null>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  // Android's back gesture and the browser's Back close the viewer, and only it (navigation/contextBack.ts).
  useContextBack(true, stageRef, onClose, OVERLAY_PRIORITY)
  const imageRef = useRef<HTMLImageElement>(null)
  const pointersRef = useRef(new Map<number, Point>())
  const transformRef = useRef<ImageTransform>({ scale: 1, x: 0, y: 0 })
  const pinchRef = useRef<PinchGesture | null>(null)
  const panRef = useRef<PanGesture | null>(null)
  const swipeRef = useRef<SwipeGesture | null>(null)
  const availableAssets = assets.filter((asset) => asset.state !== 'deleted'
    && (assetPreviewBlob(asset) || remoteUrls.has(asset.id)))
  const currentIndex = availableAssets.findIndex((asset) => asset.id === current.assetId)

  const currentAsset = availableAssets[currentIndex]
  useEffect(() => {
    if (!currentAsset || !loadFullImage || fullImageIds.current.has(currentAsset.id)) return
    let active = true
    const id = currentAsset.id
    const localMaster = currentAsset.processedMasterBlob ?? currentAsset.sourceBlob
    let request = fullImageRequests.current.get(id)
    if (!request) {
      request = localMaster ? Promise.resolve(localMaster) : loadFullImage(currentAsset)
      fullImageRequests.current.set(id, request)
    }
    setFullImageStatus({ id, state: 'loading' })
    void request.then(blob => {
      if (!active) return
      const url = URL.createObjectURL(blob)
      ownedSources.current.add(url)
      sourceCache.current.set(id, url)
      fullImageIds.current.add(id)
      setCurrent(previous => previous.assetId === id ? { ...previous, source: url } : previous)
      setFullImageStatus({ id, state: 'ready' })
    }).catch(() => {
      fullImageRequests.current.delete(id)
      if (active) setFullImageStatus({ id, state: 'failed' })
    })
    return () => { active = false }
  }, [currentAsset, loadFullImage])

  const previousAsset = availableAssets[currentIndex - 1]
  const nextAsset = availableAssets[currentIndex + 1]

  const resolveSource = useCallback((asset: NoteAsset) => {
    const cached = sourceCache.current.get(asset.id)
    if (cached) return cached
    let url = remoteUrls.get(asset.id)
    const blob = assetPreviewBlob(asset)
    if (blob) {
      try {
        url = URL.createObjectURL(blob)
        ownedSources.current.add(url)
      } catch { /* Retain the remote preview if local URLs are unavailable. */ }
    }
    if (url) sourceCache.current.set(asset.id, url)
    return url
  }, [remoteUrls])

  useLayoutEffect(() => {
    const adjacent: Record<string, string> = {}
    for (const asset of [previousAsset, nextAsset]) {
      if (!asset) continue
      const url = resolveSource(asset)
      if (url) adjacent[asset.id] = url
    }
    setNeighbors(adjacent)
  }, [previousAsset, nextAsset, resolveSource])

  useEffect(() => () => {
    animationRef.current?.cancel()
    for (const url of ownedSources.current) URL.revokeObjectURL(url)
    ownedSources.current.clear()
    fullImageIds.current.clear()
    sourceCache.current = new Map([[assetId, source]])
  }, [])

  useLayoutEffect(() => {
    // Commit the new center slide and reset the track in the same paint.
    animationRef.current?.cancel()
    animationRef.current = null
    dragRef.current = 0
    trackRef.current?.style.setProperty('--lightbox-drag', '0px')
  }, [current.assetId])

  const fitImageToStage = useCallback(() => {
    const stage = slideRef.current
    const image = imageRef.current
    if (!stage || !image || !image.naturalWidth || !image.naturalHeight) return
    const stageStyle = window.getComputedStyle(stage)
    const horizontalPadding = (Number.parseFloat(stageStyle.paddingLeft) || 0)
      + (Number.parseFloat(stageStyle.paddingRight) || 0)
    const verticalPadding = (Number.parseFloat(stageStyle.paddingTop) || 0)
      + (Number.parseFloat(stageStyle.paddingBottom) || 0)
    const fitted = calculateFittedImageSize(
      image.naturalWidth,
      image.naturalHeight,
      Math.max(0, stage.clientWidth - horizontalPadding),
      Math.max(0, stage.clientHeight - verticalPadding),
    )
    if (!fitted.width || !fitted.height) return
    image.style.width = `${fitted.width}px`
    image.style.height = `${fitted.height}px`
  }, [])

  useEffect(() => {
    const composer = document.querySelector<HTMLElement>('.composer')
    if (!composer) return
    const wasInert = composer.inert
    composer.inert = true
    return () => {
      composer.inert = wasInert
    }
  }, [])

  useEffect(() => {
    const stage = stageRef.current
    if (!stage || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(fitImageToStage)
    observer.observe(stage)
    return () => observer.disconnect()
  }, [fitImageToStage])

  const applyTransform = useCallback((next: ImageTransform) => {
    const normalized = next.scale === 1 ? { scale: 1, x: 0, y: 0 } : next
    transformRef.current = normalized
    if (imageRef.current) {
      imageRef.current.style.transform = `translate3d(${normalized.x}px, ${normalized.y}px, 0) scale(${normalized.scale})`
    }
  }, [])

  const navigate = (direction: -1 | 1) => {
    const next = availableAssets[currentIndex + direction]
    if (!next) return
    const nextSource = resolveSource(next)
    if (!nextSource) return
    pointersRef.current.clear()
    pinchRef.current = null
    panRef.current = null
    swipeRef.current = null
    applyTransform({ scale: 1, x: 0, y: 0 })
    if (imageRef.current) {
      imageRef.current.style.width = ''
      imageRef.current.style.height = ''
    }
    setCurrent({ assetId: next.id, alt: next.originalFileName ?? 'Note image', source: nextSource })
  }

  const settleSwipe = (direction: -1 | 0 | 1) => {
    if (animationRef.current) return
    const track = trackRef.current
    const width = stageRef.current?.clientWidth ?? 0
    const target = direction === 0 ? null : availableAssets[currentIndex + direction]
    const resolvedDirection = target && resolveSource(target) ? direction : 0
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (!track || !width || !track.animate || reduced) {
      dragRef.current = 0
      track?.style.setProperty('--lightbox-drag', '0px')
      if (resolvedDirection) navigate(resolvedDirection)
      return
    }
    const animation = track.animate([
      { transform: `translate3d(${-width + dragRef.current}px, 0, 0)` },
      { transform: `translate3d(${-width - resolvedDirection * width}px, 0, 0)` },
    ], {
      duration: APP_SETTINGS.interactions.imageLightboxSlideDurationMs,
      easing: 'cubic-bezier(0.22, 1, 0.36, 1)',
      fill: 'forwards',
    })
    animationRef.current = animation
    animation.onfinish = () => {
      if (resolvedDirection) navigate(resolvedDirection)
      else {
        track.style.setProperty('--lightbox-drag', '0px')
        dragRef.current = 0
        animation.cancel()
        animationRef.current = null
      }
    }
  }

  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return

    const handleWheel = (event: globalThis.WheelEvent) => {
      event.preventDefault()
      const transform = transformRef.current
      applyTransform({
        ...transform,
        scale: clampImageScale(
          transform.scale * (event.deltaY < 0 ? 1.12 : 0.88),
        ),
      })
    }

    stage.addEventListener('wheel', handleWheel, { passive: false })
    return () => stage.removeEventListener('wheel', handleWheel)
  }, [applyTransform])

  const beginPinch = useCallback((stage: HTMLElement) => {
    const points = [...pointersRef.current.values()]
    if (points.length < 2) return
    const pair: [Point, Point] = [points[0]!, points[1]!]
    const center = centerBetween(pair)
    const stageRect = stage.getBoundingClientRect()
    const transform = transformRef.current
    const centerX = center.x - (stageRect.left + stageRect.width / 2)
    const centerY = center.y - (stageRect.top + stageRect.height / 2)
    pinchRef.current = {
      distance: Math.max(1, distanceBetween(pair)),
      focalX: (centerX - transform.x) / transform.scale,
      focalY: (centerY - transform.y) / transform.scale,
      scale: transform.scale,
    }
    panRef.current = null
  }, [])

  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.currentTarget.focus({ preventScroll: true })
    if (animationRef.current) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })
    if (pointersRef.current.size >= 2) {
      swipeRef.current = null
      dragRef.current = 0
      trackRef.current?.style.setProperty('--lightbox-drag', '0px')
      beginPinch(event.currentTarget)
      return
    }
    const transform = transformRef.current
    swipeRef.current = event.pointerType === 'touch' && transform.scale === 1
      ? { pointerId: event.pointerId, x: event.clientX, y: event.clientY, horizontal: null }
      : null
    panRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      transformX: transform.x,
      transformY: transform.y,
    }
  }

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    if (!pointersRef.current.has(event.pointerId)) return
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY })

    const points = [...pointersRef.current.values()]
    const pinch = pinchRef.current
    if (points.length >= 2 && pinch) {
      const pair: [Point, Point] = [points[0]!, points[1]!]
      const center = centerBetween(pair)
      const stageRect = event.currentTarget.getBoundingClientRect()
      const centerX = center.x - (stageRect.left + stageRect.width / 2)
      const centerY = center.y - (stageRect.top + stageRect.height / 2)
      const scale = clampImageScale(
        pinch.scale * (distanceBetween(pair) / pinch.distance),
      )
      applyTransform({
        scale,
        x: centerX - pinch.focalX * scale,
        y: centerY - pinch.focalY * scale,
      })
      return
    }

    const swipe = swipeRef.current
    if (swipe?.pointerId === event.pointerId && transformRef.current.scale === 1) {
      const dx = event.clientX - swipe.x
      const dy = event.clientY - swipe.y
      if (swipe.horizontal === null && Math.hypot(dx, dy) >= APP_SETTINGS.interactions.imageLightboxDragSlopPx) {
        swipe.horizontal = Math.abs(dx) > Math.abs(dy)
      }
      if (swipe.horizontal) {
        const hasNeighbor = dx < 0 ? Boolean(nextAsset) : Boolean(previousAsset)
        const width = stageRef.current?.clientWidth ?? 0
        const offset = hasNeighbor ? dx : dx * APP_SETTINGS.interactions.imageLightboxEdgeResistance
        dragRef.current = width ? Math.max(-width, Math.min(width, offset)) : offset
        trackRef.current?.style.setProperty('--lightbox-drag', `${dragRef.current}px`)
      }
      return
    }

    const pan = panRef.current
    if (pan?.pointerId === event.pointerId && transformRef.current.scale > 1) {
      applyTransform({
        ...transformRef.current,
        x: pan.transformX + event.clientX - pan.x,
        y: pan.transformY + event.clientY - pan.y,
      })
    }
  }

  const handlePointerEnd = (event: PointerEvent<HTMLDivElement>) => {
    const swipe = swipeRef.current
    const deltaX = event.clientX - (swipe?.x ?? event.clientX)
    const deltaY = event.clientY - (swipe?.y ?? event.clientY)
    const shouldNavigate = event.type === 'pointerup'
      && swipe?.pointerId === event.pointerId
      && swipe.horizontal !== false
      && pointersRef.current.size === 1
      && transformRef.current.scale === 1
      && Math.abs(deltaX) >= APP_SETTINGS.interactions.imageLightboxSwipeDistancePx
      && Math.abs(deltaX) > Math.abs(deltaY)
    pointersRef.current.delete(event.pointerId)
    pinchRef.current = null
    swipeRef.current = null
    const [remaining] = pointersRef.current.entries()
    if (remaining) {
      const [pointerId, point] = remaining
      const transform = transformRef.current
      panRef.current = {
        pointerId,
        x: point.x,
        y: point.y,
        transformX: transform.x,
        transformY: transform.y,
      }
    } else {
      panRef.current = null
    }
    if (shouldNavigate) settleSwipe(deltaX < 0 ? 1 : -1)
    else if (dragRef.current) settleSwipe(0)
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault()
      event.stopPropagation()
      settleSwipe(event.key === 'ArrowRight' ? 1 : -1)
      return
    }
    if (event.key !== 'Escape') return
    event.preventDefault()
    event.stopPropagation()
    onClose()
  }

  return createPortal(
    <div
      className="image-lightbox visual-viewport-frame"
      role="dialog"
      aria-modal="true"
      aria-label="Maximized image view"
      onKeyDownCapture={handleKeyDown}
    >
      <div
        ref={stageRef}
        className="image-lightbox__stage"
        tabIndex={-1}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerEnd}
        onPointerCancel={handlePointerEnd}
        onDoubleClick={() => applyTransform(
          transformRef.current.scale > 1
            ? { scale: 1, x: 0, y: 0 }
            : { scale: 2, x: 0, y: 0 },
        )}
      >
        <div ref={trackRef} className="image-lightbox__track">
          <div className="image-lightbox__slide" aria-hidden="true">
            {previousAsset && neighbors[previousAsset.id] && <img src={neighbors[previousAsset.id]} alt="" draggable={false} />}
          </div>
          <div ref={slideRef} className="image-lightbox__slide" data-current="true">
            <img
              ref={imageRef}
              src={current.source}
              alt={current.alt}
              draggable={false}
              onLoad={fitImageToStage}
            />
          </div>
          <div className="image-lightbox__slide" aria-hidden="true">
            {nextAsset && neighbors[nextAsset.id] && <img src={neighbors[nextAsset.id]} alt="" draggable={false} />}
          </div>
        </div>
      </div>
      <button
        type="button"
        className="image-lightbox__close"
        onClick={onClose}
        aria-label="Close maximized image"
        title="Close image (Esc)"
        autoFocus
      >
        <Icon name="close" />
      </button>
      <div className="image-lightbox__pagination" aria-label="Photo position">
        <div className="image-lightbox__dots" aria-hidden="true">
          {availableAssets.map((asset, index) => (
            <span key={asset.id} className="image-lightbox__dot" data-active={index === currentIndex ? 'true' : undefined} />
          ))}
        </div>
        <span className="sr-only image-lightbox__status" aria-live="polite">{
          fullImageStatus?.id === current.assetId && fullImageStatus.state === 'loading'
            ? 'Loading full image…'
            : fullImageStatus?.id === current.assetId && fullImageStatus.state === 'failed'
              ? 'Full image unavailable · showing preview'
              : `Image ${currentIndex + 1} of ${availableAssets.length}`
        }</span>
      </div>
    </div>,
    document.body,
  )
}
